import { designVariables } from "@studio3t/design-variables";
import {
  BadgeConfirmationMinimal,
  BadgeErrorMinimal,
  BadgeSecondaryMinimal,
  BadgeSecondarySubtle,
  ButtonSize,
  GenericButton,
  Icon,
  IconSVG,
} from "@studio3t/generic-components";
import type { ComponentType, ReactNode } from "react";
import { Fragment, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled, { type DefaultTheme, useTheme } from "styled-components";
import { SkeletonBar, SkeletonPill } from "@/components/Skeleton";
import { useFleetMetrics, useJobs } from "@/hooks/useApi";
import type { JobMetadata } from "@/types/api";
import { formatBytes, formatNumber, formatRate } from "@/utils/format";
import {
  CardFooter,
  CardHeader,
  CardTitle,
  DOCS_URL,
  EmptyMessage,
  FooterLink,
  GUTTER,
} from "./dashboardCards";
import {
  aggregateJobMetrics,
  type HealthKind,
  healthSegments,
} from "./dashboardMetrics";
import { deriveJobControlState } from "./pipelineControls";
import { normalizeState } from "./pipelineHelpers";

const BADGE = designVariables.ui.badge;
const SEMANTIC = designVariables.ui.semantic;
const BAR_HEIGHT = 30;
const SINGLE_LINE = designVariables.type.singleLine;
const CORNER = designVariables.primitives.corner;
const GREEN = designVariables.primitives.green;
const RED = designVariables.primitives.red;
const NEUTRALS = designVariables.primitives.neutrals;

// Natural height: the status rows and the fleet tiles stack right after
// one another (Figma 15496-285937 puts the tiles a spacingl under the
// rows); the Spacer after them takes the card's leftover height, so a
// tall card grows the gap ABOVE the footer, never inside the content.
const Middle = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;

const Spacer = styled.div`
  flex: 1;
`;

// A content row of the card: 40px tall at the card's gutters (Figma
// 15697-237446 / 237452). The badges row may wrap on a narrow card, so it
// grows past 40px instead of squeezing the chips.
const Line = styled.div<{ $wrap?: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: ${(p) => (p.$wrap ? "wrap" : "nowrap")};
  gap: ${SEMANTIC.spacingxs}px ${SEMANTIC.spacings}px;
  min-height: 40px;
  padding: 0 ${GUTTER}px;
  flex-shrink: 0;
`;

// The status chip and its count read as one joined capsule (Figma
// 14879-258886): only the outer corners are rounded, and the chip's border
// forms the seam between the two.
const BadgeGroup = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  & > :first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  & > :last-child {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

// The stacked status bar (Figma "Bridge_Card2/progress", 15697-238229):
// one 30px track, every status with pipelines a segment sized by its
// share, a 2px app-background gap between neighbours, the outer corners
// rounded. It spans the row, so it shrinks with the card.
const StatusBar = styled.div`
  display: flex;
  align-items: stretch;
  flex: 1 1 auto;
  min-width: 0;
  height: ${BAR_HEIGHT}px;
  background: ${NEUTRALS.alpha.b2};
  border-radius: ${CORNER.s}px;
  overflow: hidden;
`;

const Segment = styled.div<{ $share: number; $bg: string; $border: string }>`
  flex: ${(p) => p.$share} 1 0;
  min-width: 4px;
  box-sizing: border-box;
  background: ${(p) => p.$bg};
  border: 1px solid ${(p) => p.$border};

  &:first-child {
    border-radius: ${CORNER.s}px 0 0 ${CORNER.s}px;
  }

  &:last-child {
    border-radius: 0 ${CORNER.s}px ${CORNER.s}px 0;
  }

  &:only-child {
    border-radius: ${CORNER.s}px;
  }
`;

const SegmentGap = styled.div`
  flex: 0 0 2px;
  background: ${(p) => p.theme.semanticAppBackground};
`;

// ── Fleet totals (Figma 14879-250781, node 15496-285937) ──
//
// The stat tiles under the status rows: EVENTS/BYTES PROCESSED side by
// side, THROUGHPUT on its own line. A tile is a b2-tinted strip with the
// uppercase label (and its "all-time" window note) on the left and the
// bordered value box flushed right.

const Stats = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SEMANTIC.spacingxs}px;
  /* semantic/spacingxl above the tiles (Figma 15697-237464) — the same
     40px as the card's gutters. */
  padding: ${GUTTER}px ${GUTTER}px ${SEMANTIC.spacingl}px;
  flex-shrink: 0;
`;

// Wrapping row: two tiles side by side while the card can hold them
// (the 240px basis), stacked once it can't.
const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SEMANTIC.spacingxs}px;
`;

const StatTile = styled.div`
  flex: 1 1 240px;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SEMANTIC.spacings}px;
  padding-left: ${SEMANTIC.spacingl}px;
  background: ${NEUTRALS.alpha.b2};
  overflow: hidden;
`;

const StatLabelBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const StatLabel = styled.div`
  font-size: ${SINGLE_LINE.small.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.small.medium.fontWeight};
  line-height: 1.2;
  text-transform: uppercase;
  color: ${(props) => props.theme.semanticTextLight};
`;

const StatWindow = styled.div`
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  line-height: 1.2;
  color: ${(props) => props.theme.semanticTextDisabled};
`;

const StatValue = styled.div<{ $wide?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  width: ${(props) => (props.$wide ? 280 : 120)}px;
  max-width: 60%;
  flex-shrink: 1;
  box-sizing: border-box;
  padding: 0 ${SEMANTIC.spacingl}px;
  border: 1px solid ${NEUTRALS.alpha.b10};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  color: ${(props) => props.theme.semanticTextBody};
  white-space: nowrap;
`;

function StatCell({
  label,
  window,
  value,
  wide,
}: {
  label: string;
  window?: string;
  value: ReactNode;
  wide?: boolean;
}) {
  return (
    <StatTile>
      <StatLabelBlock>
        <StatLabel>{label}</StatLabel>
        {window && <StatWindow>{window}</StatWindow>}
      </StatLabelBlock>
      <StatValue $wide={wide}>{value}</StatValue>
    </StatTile>
  );
}

// The totals render as soon as ANY pipeline's metrics settle and refine
// as the rest answer; until the first settles the value boxes hold
// skeletons. Rendered as its own block after the status rows (never
// inside them), so the tiles always keep their full — possibly wrapped —
// height.
function HealthStats({ jobs }: { jobs: JobMetadata[] }) {
  const jobNames = useMemo(() => jobs.map((j) => j.name), [jobs]);
  const { metrics, answered } = useFleetMetrics(jobNames);
  const totals = aggregateJobMetrics(metrics);
  const pending = answered === 0;
  const value = (text: string) =>
    pending ? <SkeletonBar $width="40px" /> : text;
  return (
    <Stats>
      <StatsRow>
        <StatCell
          label="Events processed"
          window="all-time"
          value={value(formatNumber(totals.events))}
        />
        <StatCell
          label="Bytes processed"
          window="all-time"
          value={value(formatBytes(totals.bytes))}
        />
      </StatsRow>
      <StatsRow>
        <StatCell
          label="Throughput"
          wide
          value={value(`${formatRate(totals.throughput)} events/sec`)}
        />
      </StatsRow>
    </Stats>
  );
}

const StateMessage = styled.div`
  padding: ${GUTTER}px 0;
  text-align: center;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-style: italic;
  color: ${(props) => props.theme.semanticTextLight};
`;

// Buckets follow the same state derivation as the pipelines tables; the
// transitional states count toward the state they are heading to.
function bucketFor(job: JobMetadata): HealthKind {
  const state = deriveJobControlState(
    normalizeState(job.state),
    normalizeState(job.desired_state),
  ).displayState;
  if (state === "Running" || state === "Starting") return "running";
  if (state === "Failed") return "failed";
  return "stopped";
}

const ROWS: {
  kind: HealthKind;
  Badge: ComponentType<{ children: ReactNode }>;
  icon: Icon;
  iconColor: (theme: DefaultTheme) => string;
  fillBackground: string;
  fillBorder: string;
}[] = [
  {
    kind: "running",
    Badge: BadgeConfirmationMinimal,
    icon: Icon.Run,
    iconColor: (t) => t.badgeConfirmMinimalColor,
    fillBackground: GREEN.alpha30,
    fillBorder: GREEN.alpha40,
  },
  {
    kind: "failed",
    Badge: BadgeErrorMinimal,
    icon: Icon.Error,
    iconColor: (t) => t.badgeErrorMinimalColor,
    fillBackground: RED.alpha30,
    fillBorder: RED.alpha40,
  },
  {
    kind: "stopped",
    Badge: BadgeSecondaryMinimal,
    icon: Icon.Stop,
    iconColor: (t) => t.badgeSecondaryMinimalColor,
    fillBackground: NEUTRALS[60],
    fillBorder: NEUTRALS[80],
  },
];

const ROW_BY_KIND = Object.fromEntries(ROWS.map((r) => [r.kind, r])) as Record<
  HealthKind,
  (typeof ROWS)[number]
>;

function SkeletonLines() {
  return (
    <>
      <Line aria-hidden="true">
        <SkeletonBar $width="100%" $height={`${BAR_HEIGHT}px`} />
      </Line>
      <Line aria-hidden="true">
        {ROWS.map((row) => (
          <SkeletonPill key={row.kind} $width="80px" />
        ))}
      </Line>
    </>
  );
}

function HealthHeader({ loaded, total }: { loaded: boolean; total: number }) {
  const theme = useTheme();

  return (
    <CardHeader>
      <IconSVG
        icon={Icon.Health}
        size={20}
        colors={{ default: theme.semanticTextHeading }}
      />
      <CardTitle>Health</CardTitle>
      {loaded && (
        <BadgeSecondarySubtle>
          {total} deployed {total === 1 ? "pipeline" : "pipelines"}
        </BadgeSecondarySubtle>
      )}
    </CardHeader>
  );
}

function StatusBadge({
  row: { kind, Badge, icon, iconColor },
  count,
}: {
  row: (typeof ROWS)[number];
  count: number;
}) {
  const theme = useTheme();
  return (
    <BadgeGroup>
      <Badge>
        <IconSVG
          icon={icon}
          size={BADGE.icon}
          colors={{ default: iconColor(theme) }}
        />
        {kind}
      </Badge>
      <BadgeSecondarySubtle>{count}</BadgeSecondarySubtle>
    </BadgeGroup>
  );
}

// The stacked bar, then the status chips with their counts underneath.
function HealthLines({
  loading,
  error,
  counts,
}: {
  loading: boolean;
  error: Error | null;
  counts: Record<HealthKind, number>;
}) {
  if (loading) return <SkeletonLines />;
  if (error) return <StateMessage>Failed to load pipelines</StateMessage>;
  const segments = healthSegments(counts);
  return (
    <>
      <Line>
        <StatusBar
          role="img"
          aria-label={segments.map((s) => `${s.count} ${s.kind}`).join(", ")}
        >
          {segments.map(({ kind, count }, i) => (
            <Fragment key={kind}>
              {i > 0 && <SegmentGap />}
              <Segment
                $share={count}
                $bg={ROW_BY_KIND[kind].fillBackground}
                $border={ROW_BY_KIND[kind].fillBorder}
              />
            </Fragment>
          ))}
        </StatusBar>
      </Line>
      <Line $wrap>
        {ROWS.map((row) => (
          <StatusBadge key={row.kind} row={row} count={counts[row.kind]} />
        ))}
      </Line>
    </>
  );
}

function HealthFooter() {
  const navigate = useNavigate();

  return (
    <CardFooter>
      <FooterLink
        size="L"
        href={DOCS_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        Read more about pipelines
      </FooterLink>
      <GenericButton
        text="Add pipeline"
        type="secondary"
        size={ButtonSize.Small}
        icon={Icon.Plus}
        iconPosition="leading"
        onClick={() => navigate("/studio")}
      />
    </CardFooter>
  );
}

/** The card's content between header and footer: skeletons or the
 *  engine's error from the lines, the one line saying nothing is deployed
 *  (Figma 16102-26437), or the status bar with the fleet totals under it. */
function HealthBody({
  jobs,
  loading,
  error,
}: {
  jobs: JobMetadata[] | undefined;
  loading: boolean;
  error: Error | null;
}) {
  const counts = useMemo(() => countByKind(jobs), [jobs]);
  if (!settled(jobs, loading, error)) {
    return (
      <Middle>
        <HealthLines loading={loading} error={error} counts={counts} />
      </Middle>
    );
  }
  if (jobs.length === 0) {
    return <EmptyMessage>You have not deployed any pipeline yet</EmptyMessage>;
  }
  return (
    <>
      <Middle>
        <HealthLines loading={false} error={null} counts={counts} />
      </Middle>
      <HealthStats jobs={jobs} />
    </>
  );
}

/** Whether the jobs list has answered, and well: only then does the card
 *  know whether there is anything deployed. */
function settled(
  jobs: JobMetadata[] | undefined,
  loading: boolean,
  error: Error | null,
): jobs is JobMetadata[] {
  return !loading && error === null && jobs !== undefined;
}

function countByKind(
  jobs: JobMetadata[] | undefined,
): Record<HealthKind, number> {
  const result: Record<HealthKind, number> = {
    running: 0,
    failed: 0,
    stopped: 0,
  };
  for (const job of jobs ?? []) result[bucketFor(job)] += 1;
  return result;
}

export function DashboardHealth() {
  const { data: jobs, isLoading, error } = useJobs();
  const loading = isLoading && !jobs;

  return (
    <>
      <HealthHeader loaded={Boolean(jobs)} total={jobs?.length ?? 0} />
      <HealthBody jobs={jobs} loading={loading} error={error} />
      <Spacer />
      <HealthFooter />
    </>
  );
}
